import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useTerminalLoadQuery, useTerminalUpdateMutation } from "src/app/features/Terminal/services/server/TerminalApi";
import { updateWatchedFieldsUtils } from "src/app/features/Terminal/TerminalProfileToTerminalUtils";
import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import { useTerminalProfileLoadQuery } from "src/app/features/TerminalProfile/services/server/TerminalProfileApi";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showSuccessNotification } from "../../../../../components/NotificationAlerts";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import type { ID } from "../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";

export const TerminalEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const [formData, setFormData] = useState<TerminalEntity>();
    const prevProfileIdRef = useRef<ID | undefined>(undefined);
    const formDataRef = useRef(formData);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("terminal");
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useTerminalLoadQuery({ entityId: id });

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
            formDataRef.current = loadFormData;
            prevProfileIdRef.current = loadFormData.terminalProfileId;
        }
    }, [loadFormData]);

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback((newData: TerminalEntity) => {
        setFormData({
            ...newData,
            serialProtocolCheckReadOnly: formDataRef.current?.serialProtocolCheckReadOnly,
            posIpAddressCheckReadOnly: formDataRef.current?.posIpAddressCheckReadOnly,
            posPortCheckReadOnly: formDataRef.current?.posPortCheckReadOnly,
            deviceApiTokenCheckReadOnly: formDataRef.current?.deviceApiTokenCheckReadOnly,
            abortNeedsRequestIdCheckReadOnly: formDataRef.current?.abortNeedsRequestIdCheckReadOnly,
            cloudFlowEnabledCheckReadOnly: formDataRef.current?.cloudFlowEnabledCheckReadOnly,
        });
        formDataRef.current = {
            ...newData,
            serialProtocolCheckReadOnly: formDataRef.current?.serialProtocolCheckReadOnly,
            posIpAddressCheckReadOnly: formDataRef.current?.posIpAddressCheckReadOnly,
            posPortCheckReadOnly: formDataRef.current?.posPortCheckReadOnly,
            deviceApiTokenCheckReadOnly: formDataRef.current?.deviceApiTokenCheckReadOnly,
            abortNeedsRequestIdCheckReadOnly: formDataRef.current?.abortNeedsRequestIdCheckReadOnly,
            cloudFlowEnabledCheckReadOnly: formDataRef.current?.cloudFlowEnabledCheckReadOnly,
        };
    }, []);

    const { data: terminalProfileEntity } = useTerminalProfileLoadQuery(
        { entityId: formData?.terminalProfileId ? `${formData.terminalProfileId}` : undefined },
        { skip: !formData?.terminalProfileId },
    );

    // HANDLE WATCHED FIELDS
    const updateWatchedFields = useCallback(() => {
        const currentProfileId = formData?.terminalProfileId;

        if (!currentProfileId || !terminalProfileEntity || !prevProfileIdRef) return null;

        prevProfileIdRef.current = currentProfileId;

        const profile = terminalProfileEntity;
        if (!profile || !formDataRef.current) return null;

        return updateWatchedFieldsUtils(profile, formDataRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [terminalProfileEntity]);

    useEffect(() => {
        const currentProfileId = formData?.terminalProfileId;

        if (!terminalProfileEntity || !prevProfileIdRef || !currentProfileId) {
            return;
        }

        const prevProfileId = prevProfileIdRef.current;

        if (currentProfileId !== prevProfileId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [formData, terminalProfileEntity, updateWatchedFields]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useTerminalUpdateMutation();

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formDataRef.current) return;

            const result = await saveFormData({ data: formDataRef.current });
            const hasError = "error" in result;

            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "Terminal",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.TERMINALS}`);
            }
        },
        [saveFormData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && formData && (
                <FormManager<TerminalEntity>
                    name="TerminalEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
